import {Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output, ViewChild} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {EntiteTreeNode} from '@Services/gestion-universite/entite-tree-node/entite-tree-node.model';
import {EntiteTreeNodeService} from '@Services/gestion-universite/entite-tree-node/entite-tree-node.service';
import {UntypedFormControl} from '@angular/forms';
import {debounceTime, distinctUntilChanged, filter, finalize, switchMap} from 'rxjs/operators';
import {Observable} from 'rxjs';

import {SubSink} from '@Common/core/utils/subsink';
import {PubSubService} from "@Common/core/services";

@Component({
  selector: 'app-search-arbo-entite',
  templateUrl: './search-entite-arbo.component.html',
  styleUrls: ['./search-entite-arbo.component.css']
})
  export class SearchEntiteArboComponent implements OnInit, OnDestroy {
  @Input() searchBy = 'dircmp';
  @Input() parentId = 1;
  @Input() parentLabel: string = null;
  @Input() hasChildren = false;
  @Input() activated: boolean = null;

  @Output() entiteSelectedEvent: EventEmitter<EntiteTreeNode> = new EventEmitter<EntiteTreeNode>();

  entiteSelected: EntiteTreeNode;
  entites: EntiteTreeNode[];
  entitesResults: Observable<EntiteTreeNode[]>;
  listEntites: EntiteTreeNode[] = [];

  DEFAULT_PARENT = 1;
  DEFAULT_DEPTHS: number[] = [2, 3];

  types: string[] = ['adm', 'cmp', 'dep']; // adm:cmp:dep:lab
  depths: number[] = [];

  node = null;
  event = null;

  entiteForm = new UntypedFormControl();

  titleSearch = 'Choix d\'une direction ou composante';
  placeholder = 'Je recherche ma direction ou composante...au moins 2 caractères';
  tooltip = 'Par exemple : DIREC donnera les DIRECTIONS, UFR les UFR, etc';

  subSink = new SubSink();

  constructor(
    @Optional() public dialogRef: MatDialogRef<SearchEntiteArboComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private entiteTreeNodeService: EntiteTreeNodeService,
    private pubSubSvc: PubSubService) {
    this.parentId = this.DEFAULT_PARENT;
    this.types = ['adm', 'cmp'];
    this.depths =  this.DEFAULT_DEPTHS;
  }

  ngOnInit() {
    this._initDataInput();
    this._initSearch();
  }

  /**
   * Prise en compte des données passées au component
   *  - soit par modale (data)
   *  - soit par @Input()
   * @private
   */
  private _initDataInput() {
    let data = { searchBy: '', parentId: 1, parentLabel: '', hasChildren: false, activated: null};

    if (this.data) {
      data = {
        parentId: this.data.parentId,
        parentLabel: this.data.parentLabel,
        hasChildren: this.data.hasChildren,
        searchBy: this.data.searchBy,
        activated: this.data.activated
      };
      this.activated = this.data.activated;
    } else {
      data = {
        parentId: this.parentId,
        parentLabel: this.parentLabel,
        hasChildren: this.hasChildren,
        searchBy: this.searchBy,
        activated: this.activated
      };
    }

    this.searchBy = data.searchBy;
    if (data.searchBy === 'svcdep') {
      this._initSearchLabel(data);
    }

    if (data.searchBy === 'dircmp') {
      this.types = ['adm', 'cmp'];
      this.depths = this.DEFAULT_DEPTHS;
    }
    this.subSink.sink = this.search('', this.activated).subscribe((_data) => {
      this.listEntites = _data;
    });
  }

  search(words, active: boolean = null) {
    const typesAdmCmp = this.types.reduce(function(acc, currentValue, idx) {
      return idx === 0 ? currentValue : `${acc}:${currentValue}`;
    }, '');
    return this.entiteTreeNodeService.searchByParentTypes(this.parentId, words, typesAdmCmp, this.depths, active);
  }

  /**
   * Initialisation champ de recherche
   * @private
   */
  private _initSearch() {
    this.entitesResults = this.entiteForm.valueChanges
      .pipe(
        finalize(() => this.pubSubSvc.publish('loading', false)),
        debounceTime(500), distinctUntilChanged(),
        filter(searchTerm => searchTerm && searchTerm.length >= 2),
        switchMap(words => {
          return this.search(words, this.activated);
        }));
  }

  /**
   * Initialisation de l'arbre des entités
   * (voir à l'enlever si on ne retient que la recherche textuel)
   * @private
   */
  private _initArbo() {
    const types = this.reduceTypes();

    if (this.parentId > 1) {
      this.entiteTreeNodeService.listByParent(this.parentId, types).subscribe((_entities) => {
        this.entites = _entities;
      });
    } else {
      this.entiteTreeNodeService
        .listByDepth(this.depths, this.types)
        .subscribe(_entities => this.entites = _entities);
    }
  }

  /**
   * Initialisation des libellés, types de recherches
   * @param data
   * @private
   */
  private _initSearchLabel(data) {
    this.titleSearch = `Choix d'un service ou département ou labo`;
    this.placeholder = 'Je recherche le service ou département ou labo...au moins 2 caractères';

    if (data.hasChildren && 'parentId' in data) {
      this.types = ['adm', 'cmp', 'dep', 'lab'];
      this.depths = [];
      this.parentId = data.parentId;
      this.parentLabel = `dans ${data.parentLabel}`;
    } else {
      this.types = ['adm', 'cmp', 'dep', 'lab'];
      this.parentId = this.DEFAULT_PARENT;
      this.depths = [];
      this.parentLabel = 'dans Université Paris 8';
    }
  }

  getDisplayEntite(entite: EntiteTreeNode) {
    let label = `${entite.name}`;

    if (entite.code_manque) {
      label = `${label} (${entite.label_entite.toLowerCase()}, code ${entite.code_manque})`;
    } else {
      label = `${label} (${entite.label_entite.toLowerCase()}, code ${entite.code})`;
    }

    return label;
  }

  onSearchSelectEntite(item) {
    this.entiteSelected = item;
  }

  onSelectEntite(item) {
    this.entiteSelected = item.value;
  }

  canSelect() {
    return !(!this.entiteSelected || this.entiteSelected.type_entite === 'univ');
  }

  reduceTypes() {
    return this.types.reduce(function(acc, currentValue, idx) {
      return idx === 0 ? currentValue : `${acc}:${currentValue}`;
    }, '');
  }

  onActivate(event) {
    const entite = event.node.data;

    if (entite && entite.type_entite !== 'univ') {
      this.entiteSelected = entite;
    }
  }

  close() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  select() {
    const data = this.entiteSelected;

    if (this.dialogRef) {
      this.dialogRef.close(data);
    } else {
      this.entiteSelectedEvent.emit(data);
    }
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }
}
